import type { Deadline } from '@/model/repository/manager/parentContacts/AttendanceContactDeadlineRepository';

type DayOfWeek = 'sun' | 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat';

const DEFAULT_TIME_SAME_DAY = '09:00';
const DEFAULT_TIME_PREVIOUS_DAY = '18:00';
const DEFAULT_DAY_OF_WEEK: DayOfWeek = 'sun';
const DEFAULT_DAY_OF_MONTH = 1;

const DAYS_BEFORE_KIND_PREFIX = 'days_before_';

const defaultTimeForDays = (days: number): string => (days === 0 ? DEFAULT_TIME_SAME_DAY : DEFAULT_TIME_PREVIOUS_DAY);

/**
 * Deadline を左プルダウン（期日種別）の UI 軸キーに変換する。
 *
 * Deadline 型は 4 種だが、左プルダウンは days_before の days 値ごとに
 * 'days_before_0' .. 'days_before_3' の 4 種へ分割される（合計 7 種）。
 */
export const toFormKind = (d: Deadline): string => {
  switch (d.type) {
    case 'none':
      return 'none';
    case 'days_before':
      return `${DAYS_BEFORE_KIND_PREFIX}${d.days}`;
    case 'previous_week':
      return 'previous_week';
    case 'previous_month':
      return 'previous_month';
  }
};

/**
 * 左プルダウンの UI 軸キーから Deadline を再構築する。
 *
 * 右プルダウンが持つ値（time / day_of_week / day）はすべて既定値で初期化する。
 * ng-change は値が変わったときだけ発火するため、同 kind への遷移は構造的に起きず、
 * 「同 type なら前回値を保持する」ロジックは実利用パスで到達しないため持たない。
 *
 * 既定値は利用申請ON フォーム（v2）の init_display_value と同じ仕様:
 *   - 当日(days_before_0) → time='09:00'
 *   - 前日/2日前/3日前    → time='18:00'
 *   - previous_week       → day_of_week='sun'
 *   - previous_month      → day=1
 *
 * @throws Error 未知の kind が渡された場合
 */
export const fromFormKind = (kind: string): Deadline => {
  if (kind === 'none') {
    return { type: 'none' };
  }
  if (kind.startsWith(DAYS_BEFORE_KIND_PREFIX)) {
    const days = Number(kind.slice(DAYS_BEFORE_KIND_PREFIX.length));
    return { type: 'days_before', days, time: defaultTimeForDays(days) };
  }
  if (kind === 'previous_week') {
    return { type: 'previous_week', day_of_week: DEFAULT_DAY_OF_WEEK };
  }
  if (kind === 'previous_month') {
    return { type: 'previous_month', day: DEFAULT_DAY_OF_MONTH };
  }
  throw new Error(`Unknown deadline form kind: ${kind}`);
};
